import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Award } from "lucide-react";
import { api, IMAGES } from "../lib/api";
import { buildSections } from "../lib/laCarteData";

const GOLD = "#9a7b2d";

const Divider = () => (
  <div className="flex items-center justify-center gap-3 my-3" aria-hidden="true">
    <span className="h-px w-16 bg-[#c9a84c]/60" />
    <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a84c]" />
    <span className="h-px w-16 bg-[#c9a84c]/60" />
  </div>
);

const ItemRow = ({ item }) => (
  <div className="py-2.5" data-testid={`carte-item-${item.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
    <div className="flex items-baseline gap-2">
      <span className="font-serif-elegant text-[17px] font-semibold text-[#1c1a15]">{item.name}</span>
      <span className="flex-1 border-b border-dotted border-[#c9a84c]/50 translate-y-[-3px]" />
      <span className="font-serif-elegant text-[16px] font-bold whitespace-nowrap" style={{ color: GOLD }}>{item.price}</span>
    </div>
    {item.desc && <p className="text-[13px] text-[#6b6250] italic mt-0.5 leading-relaxed pr-10">{item.desc}</p>}
  </div>
);

const MultiPriceRow = ({ item, columns }) => (
  <div className="py-2.5">
    <div className="hidden sm:flex items-baseline gap-2">
      <span className="font-serif-elegant text-[17px] font-semibold text-[#1c1a15] flex-1">{item.name}</span>
      {item.prices.map((p, i) => (
        <span key={i} className="font-serif-elegant text-[15px] font-bold w-20 text-right whitespace-nowrap" style={{ color: p === "—" ? "#b5ab95" : GOLD }}>{p}</span>
      ))}
    </div>
    <div className="sm:hidden">
      <span className="font-serif-elegant text-[17px] font-semibold text-[#1c1a15]">{item.name}</span>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
        {item.prices.map((p, i) => p !== "—" && (
          <span key={i} className="text-[13px]">
            <span className="text-[#6b6250]">{(columns?.[i] || "").split(" ")[0]} </span>
            <span className="font-serif-elegant font-bold" style={{ color: GOLD }}>{p}</span>
          </span>
        ))}
      </div>
    </div>
    {item.desc && <p className="text-[13px] text-[#6b6250] italic mt-0.5 leading-relaxed pr-10">{item.desc}</p>}
  </div>
);

const SectionBlock = ({ section }) => (
  <section id={section.id} className="break-inside-avoid mb-12" data-testid={`carte-section-${section.id}`}>
    <div className="text-center mb-5">
      <h2 className="font-serif-elegant text-2xl sm:text-3xl font-bold tracking-wide text-[#1c1a15] uppercase">{section.title}</h2>
      {section.sub && <p className="text-xs tracking-[0.25em] uppercase mt-1" style={{ color: GOLD }}>{section.sub}</p>}
      <Divider />
    </div>
    {section.columns && (
      <div className="hidden sm:flex items-baseline gap-2 border-b border-[#c9a84c]/40 pb-1 mb-1">
        <span className="flex-1" />
        {section.columns.map((c) => (
          <span key={c} className="w-20 text-right text-[11px] uppercase tracking-wider font-semibold" style={{ color: GOLD }}>{c}</span>
        ))}
      </div>
    )}
    {section.groups
      ? section.groups.map((g) => (
          <div key={g.label} className="mb-5">
            <p className="text-center font-serif-elegant text-lg italic font-semibold mb-1" style={{ color: GOLD }}>— {g.label} —</p>
            {g.items.map((item) => <ItemRow key={item.id || item.name} item={item} />)}
          </div>
        ))
      : section.items.map((item) =>
          item.prices
            ? <MultiPriceRow key={item.id || item.name} item={item} columns={section.columns} />
            : <ItemRow key={item.id || item.name} item={item} />
        )}
  </section>
);

export default function LaCartePage() {
  const [sections, setSections] = useState([]);
  const [formules, setFormules] = useState({ formules: [], menu_enfant: null });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/menu"), api.get("/formules")])
      .then(([m, f]) => { setSections(buildSections(m.data)); setFormules(f.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "La Carte | Italian Club Champs-sur-Marne";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "Découvrez la carte de l'Italian Club à Champs-sur-Marne : formules du midi, cocktails, apéritifs, bières et boissons.");
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="bg-[#f5efe0] text-[#1c1a15] min-h-screen" data-testid="la-carte-page">
      {/* En-tête */}
      <div className="max-w-5xl mx-auto px-6 pt-14 pb-8 text-center">
        <img src={IMAGES.logo} alt="Italian Club" className="h-24 w-24 object-contain mx-auto mb-4 rounded-full bg-white shadow-[0_2px_16px_rgba(154,123,45,0.25)]" />
        <p className="text-xs tracking-[0.35em] uppercase font-semibold" style={{ color: GOLD }}>Bar · Restaurant · Live Music</p>
        <h1 className="font-serif-elegant text-4xl sm:text-5xl lg:text-6xl font-bold mt-3">La Carte de l'Italian Club</h1>
        <div className="tricolore mt-5 justify-center flex"><span /><span style={{ background: "#fff", border: "1px solid #ddd" }} /><span /></div>
        <div className="inline-flex items-center gap-2 mt-6 px-4 py-1.5 rounded-full border text-xs sm:text-sm" style={{ borderColor: GOLD, color: "#1c1a15" }} data-testid="carte-award-badge">
          <Award size={15} style={{ color: "#CE2B37" }} />
          <span><span className="font-semibold" style={{ color: GOLD }}>Restaurant Guru 2026</span> · Meilleur restaurant italien de Champs-sur-Marne</span>
        </div>
        <br />
        <Link to="/reserver" data-testid="carte-reserve-btn" className="btn-gold mt-8 !text-black">
          <CalendarCheck size={17} /> Réserver une table
        </Link>
      </div>

      {/* Formules */}
      <div className="max-w-5xl mx-auto px-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="carte-formules">
          {formules.formules.map((f, i) => (
            <div key={i} className="border-2 border-[#c9a84c] rounded-xl bg-[#fffdf6] p-7 text-center shadow-[0_2px_12px_rgba(154,123,45,0.12)]" data-testid={`carte-formule-${i}`}>
              <p className="text-xs tracking-[0.3em] uppercase font-semibold" style={{ color: GOLD }}>{f.title || "Formule Midi"}{f.subtitle ? ` · ${f.subtitle}` : ""}</p>
              <p className="font-serif-elegant text-4xl font-bold mt-2" style={{ color: GOLD }}>{f.price}</p>
              <Divider />
              <ul className="space-y-1.5">
                {f.lines.map((l) => <li key={l} className="font-serif-elegant text-lg font-semibold">{l}</li>)}
              </ul>
              {f.note && <p className="text-[12px] text-[#6b6250] italic mt-4">{f.note}</p>}
            </div>
          ))}
        </div>
        {formules.menu_enfant && (
          <div className="border border-[#c9a84c]/70 rounded-xl bg-[#fffdf6] p-6 text-center mt-6" data-testid="carte-menu-enfant">
            <p className="font-serif-elegant text-2xl font-bold">{formules.menu_enfant.title} <span style={{ color: GOLD }}>— {formules.menu_enfant.price}</span></p>
            {formules.menu_enfant.subtitle && <p className="text-xs tracking-[0.25em] uppercase mt-1" style={{ color: GOLD }}>{formules.menu_enfant.subtitle}</p>}
            {formules.menu_enfant.lines.map((l) => <p key={l} className="font-serif-elegant text-lg font-semibold mt-2">{l}</p>)}
            {formules.menu_enfant.note && <p className="text-[12px] text-[#6b6250] italic mt-1">{formules.menu_enfant.note}</p>}
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="max-w-5xl mx-auto px-6 py-14">
        {loading && <p className="text-center text-sm text-[#6b6250] italic" data-testid="carte-loading">Chargement de la carte…</p>}
        <div className="md:columns-2 md:gap-14">
          {sections.map((s) => <SectionBlock key={s.id} section={s} />)}
        </div>
      </div>

      {/* Pied de page carte */}
      <div className="max-w-5xl mx-auto px-6 pb-20 text-center">
        <Divider />
        <p className="font-serif-elegant text-xl italic" style={{ color: GOLD }}>L'Italie dans votre assiette, la musique dans votre cœur</p>
        <p className="text-xs text-[#6b6250] mt-2">Prix nets TTC, service compris · 36 Rue de Paris, 77420 Champs-sur-Marne</p>
        <Link to="/reserver" data-testid="carte-reserve-btn-bottom" className="btn-gold mt-8 !text-black">
          <CalendarCheck size={17} /> Réserver une table
        </Link>
      </div>
    </div>
  );
}
